"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { TriangleAlert as AlertTriangle, CircleCheck as CheckCircle2, Droplets, Gauge, PhoneCall, Pipette, ShieldAlert, Sparkles, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const problemsList = [{
  id: "old-pipes",
  title: "Aging & Deteriorating Pipes",
  tag: "Structural Risk",
  icon: ShieldAlert,
  issue: "Pipes older than 30–40 years (such as galvanized steel or polybutylene) degrade internally, making them brittle and prone to spontaneous bursts.",
  resolution: "Replaced with modern, durable PEX or copper lines engineered to resist degradation and temperature swings for decades."
}, {
  id: "corrosion",
  title: "Pipe Corrosion & Discolored Water",
  tag: "Water Quality",
  icon: Droplets,
  issue: "Internal rust buildup leaches heavy metals into your tap, causing brownish water, metallic tastes, and staining on porcelain fixtures.",
  resolution: "Non-corrosive piping guarantees clean, clear water and prevents hazardous chemical reactions inside the plumbing grid."
}, {
  id: "frequent-leaks",
  title: "Recurring Pin-Hole & Slab Leaks",
  tag: "Costly Damage",
  icon: AlertTriangle,
  issue: "Repeated small leaks behind walls or underneath foundation slabs lead to hidden moisture, wood rot, and expensive emergency patch repairs.",
  resolution: "Complete line replacement removes hidden weak points entirely, eliminating the constant cycle of patch fixes and drywall tear-outs."
}, {
  id: "low-pressure",
  title: "Low or Inconsistent Water Pressure",
  tag: "Daily Inconvenience",
  icon: Gauge,
  issue: "Mineral scale and sediment gradually narrow pipe diameter, restricting flow so showers trickle whenever another tap or appliance runs.",
  resolution: "Optimally sized piping restores full, balanced water volume and pressure across multiple fixtures simultaneously."
}];
const signsList = ["Multiple pipe leaks within a single 12-month period", "Flakes of rust or sediment appearing in aerators and filters", "Sudden spike in your monthly municipal water bill", "Discolored water when opening hot water taps in the morning"];
export default function RepipingProblems() {
  const prefersReducedMotion = useReducedMotion();
  const cardVariants = {
    hidden: {
      opacity: prefersReducedMotion ? 1 : 0,
      y: prefersReducedMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0
    }
  };
  return <section id="repipingproblemssolved" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 max-w-7xl">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-14">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wide">
            Permanent Plumbing Solutions
          </Badge>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground mb-4">
            Plumbing Problems Whole-Home Repiping Solves
          </h2>
          <p className="text-muted-foreground text-base md:text-lg font-sans">
            Continuous patch repairs only buy time when your plumbing system has reached its lifespan. Repiping replaces brittle, scaled lines with reliable modern piping that restores water safety, volume, and home security.
          </p>
        </div>

        {/* 4 Core Problems Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 mb-16">
          {problemsList.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} data-index={index} initial="hidden" whileInView="visible" viewport={{
            once: true,
            margin: "-50px"
          }} transition={{
            duration: 0.4,
            delay: prefersReducedMotion ? 0 : index * 0.1
          }} variants={cardVariants} className="h-full">
                <Card className="h-full border border-border shadow-sm hover:shadow-md transition-[box-shadow,border-color] bg-card text-card-foreground flex flex-col justify-between">
                  <CardHeader className="space-y-4 pb-4">
                    <div className="flex items-center justify-between">
                      <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-medium border-border">
                        {item.tag}
                      </Badge>
                    </div>
                    <div>
                      <CardTitle className="text-xl md:text-2xl font-bold font-default text-foreground">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground mt-2 text-sm leading-relaxed">
                        <strong className="text-foreground font-medium">The Symptom: </strong>
                        {item.issue}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0">
                    <div className="p-4 rounded-lg bg-muted/60 border border-border/60">
                      <div className="flex items-start gap-3">
                        <CheckCircle2 className="size-5 text-chart-4 shrink-0 mt-0.5" />
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">
                            How Repiping Fixes It
                          </p>
                          <p className="text-sm text-foreground font-medium">
                            {item.resolution}
                          </p>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Diagnostic Banner */}
        <div className="rounded-2xl border border-border bg-card p-8 lg:p-10 shadow-sm mb-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-7 space-y-4">
              <div className="inline-flex items-center gap-2 text-primary font-medium text-sm">
                <Pipette className="size-4 text-primary" />
                <span>Diagnostic Assessment in Jonesboro, GA</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-default font-bold text-foreground">
                When Is It Time to Repipe Instead of Repairing?
              </h3>
              <p className="text-muted-foreground text-sm md:text-base leading-relaxed">
                If your plumbing is older than 25 years or you notice several of the warning signs below, a comprehensive camera inspection and pressure test can determine if a full or partial repipe is necessary.
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                {signsList.map((sign, index) => <div key={index} data-index={index} className="flex items-start gap-2.5">
                    <div className="size-2 rounded-full bg-accent mt-2 shrink-0" />
                    <span className="text-sm text-foreground">{sign}</span>
                  </div>)}
              </div>
            </div>

            <div className="lg:col-span-5 bg-background rounded-xl p-6 border border-border flex flex-col justify-between space-y-6">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  <Sparkles className="size-4 text-accent" />
                  <span>Licensed Plumbing Technicians</span>
                </div>
                <h4 className="text-lg font-bold font-default text-foreground">
                  Need an Honest Pipe Inspection?
                </h4>
                <p className="text-sm text-muted-foreground">
                  We inspect your entire supply network, identify vulnerable pipe materials, and provide flat-rate options.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row lg:flex-col gap-3">
                <Button asChild size="lg" className="w-full justify-center">
                  <Link to="/contact/" className="inline-flex items-center justify-center gap-2">
                    <Wrench className="size-4" />
                    <span>Schedule Pipe Inspection</span>
                  </Link>
                </Button>

                <Button asChild variant="outline" size="lg" className="w-full justify-center">
                  <Link to="/services/" className="inline-flex items-center justify-center gap-2">
                    <span>Explore Plumbing Services</span>
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Emergency Plumber Contact Strip */}
        <div className="text-center">
          <p className="text-sm text-muted-foreground flex flex-wrap items-center justify-center gap-2">
            <span>Dealing with an active pipe rupture right now?</span>
            <Link to="/contact/" className="text-primary font-semibold hover:underline inline-flex items-center gap-1">
              <PhoneCall className="size-3.5" />
              <span>Request 24/7 Emergency Dispatch</span>
            </Link>
          </p>
        </div>
      </div>
    </section>;
}